import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, Shield, Search, RefreshCw, ArrowLeft,
  Clock, CheckCircle, AlertCircle, History, TrendingUp
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getConversations, getUserEscrowHistory, confirmEscrowReceipt } from '../../services/api';
import { getImageUrl } from '../../utils/imageUtils';
import Loading from '../../components/common/Loading';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Header from '../../components/layout/Header';
import ChatListItem from '../../components/chat/ChatListItem';
import EscrowListItem from '../../components/chat/EscrowListItem';
import StandaloneEscrowModal from '../../components/chat/StandaloneEscrowModal';
import StatsCards from '../../components/chat/StatsCards';
import EmptyState from '../../components/chat/EmptyState';

const ChatsScreen = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('chats');
  const [chats, setChats] = useState([]);
  const [escrowHistory, setEscrowHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    held: 0,
    released: 0,
    disputed: 0,
    totalAmount: 0
  });

  const [selectedEscrow, setSelectedEscrow] = useState(null);
const [showEscrowModal, setShowEscrowModal] = useState(false);

  // Add these refs for search inputs
  const mobileSearchRef = useRef(null);
  const desktopSearchRef = useRef(null);
  
  // Track if search was focused
  const [wasSearchFocused, setWasSearchFocused] = useState(false);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [chatsResponse, escrowResponse] = await Promise.all([
        getConversations(user.user_id),
        getUserEscrowHistory(user.user_id)
      ]);
      
      setChats(chatsResponse.data || []);
      
      if (escrowResponse.success) {
        const escrows = escrowResponse.data || [];
        setEscrowHistory(escrows);
        calculateStats(escrows);
      }
    } catch (error) {
      // console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEscrowHistory = async () => {
    try {
      const escrowResponse = await getUserEscrowHistory(user.user_id);
      if (escrowResponse.success) {
        const escrows = escrowResponse.data || [];
        setEscrowHistory(escrows);
        calculateStats(escrows);
      }
    } catch (error) {
      // console.error('Error fetching escrow history:', error);
    }
  };

  const calculateStats = (escrows) => {
    let total = escrows.length;
    let held = 0, released = 0, disputed = 0;
    let totalAmount = 0;

    escrows.forEach(escrow => {
      totalAmount += parseFloat(escrow.amount || 0);
      
      switch(escrow.status) {
        case 'held':
          held++;
          break;
        case 'released':
          released++;
          break;
        case 'disputed':
          disputed++;
          break;
      }
    });

    setStats({
      total,
      held,
      released,
      disputed,
      totalAmount
    });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (activeTab === 'chats') {
        const chatsResponse = await getConversations(user.user_id);
        setChats(chatsResponse.data || []);
      } else {
        await fetchEscrowHistory();
      }
    } catch (error) {
      // console.error('Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);
      
      if (diffInHours < 1) {
        const diffInMinutes = Math.floor(diffInHours * 60);
        return `${diffInMinutes}m`;
      } else if (diffInHours < 24) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (diffInHours < 48) {
        return 'Yesterday';
      } else {
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      }
    } catch (error) {
      return '';
    }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '₦0';
    const numericPrice = parseFloat(price);
    if (isNaN(numericPrice)) return '₦0';
    
    return `₦${numericPrice.toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return date.toLocaleDateString('en-NG', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const navigateToChat = (chat) => {
    navigate(`/chats/${chat.conversation_id}`, {
      state: {
        // Seller information
        sellerId: chat.seller_id,
        sellerName: chat.seller_name || 
                   (chat.seller_first_name ? `${chat.seller_first_name} ${chat.seller_last_name}` : `Seller ${chat.seller_id}`),
        sellerFirstName: chat.seller_first_name || '',
        sellerLastName: chat.seller_last_name || '',
        sellerProfilePicture: chat.seller_profile_picture || null,
        sellerStoreLogo: chat.seller_store_logo || null,
        sellerStoreName: chat.seller_store_name || null, // Add store name here
        
        // Buyer information
        buyerId: chat.buyer_id,
        buyerProfilePicture: chat.buyer_profile_picture || null,
        buyerName: chat.buyer_name || 
                   (chat.buyer_first_name ? `${chat.buyer_first_name} ${chat.buyer_last_name}` : `Buyer ${chat.buyer_id}`),
        buyerFirstName: chat.buyer_first_name || '',
        buyerLastName: chat.buyer_last_name || '',
        
        // Listing information
        listingId: chat.listing_id,
        listingTitle: chat.listing_title || 'Item Listing',
        listingPrice: chat.listing_price || '0.00',
        listingImage: chat.listing_image || null,
        
        // Conversation information
        conversationId: chat.conversation_id,
        lastMessage: chat.last_message || '',
        lastMessageTime: chat.last_message_time || chat.last_message_at,
        createdAt: chat.created_at,
        unreadCount: chat.unread_count || 0
      }
    });
  };

  const navigateToEscrow = (escrow) => {
    setSelectedEscrow(escrow);
    setShowEscrowModal(true);
  };

  useEffect(() => {
    // Focus search input when activeTab changes
    const focusSearch = () => {
      // Use timeout to ensure DOM is ready
      setTimeout(() => {
        if (window.innerWidth < 1024) { // Mobile
          if (mobileSearchRef.current) {
            mobileSearchRef.current.focus();
            // Preserve cursor position
            const cursorPos = searchQuery.length;
            mobileSearchRef.current.setSelectionRange(cursorPos, cursorPos);
          }
        } else { // Desktop
          if (desktopSearchRef.current) {
            desktopSearchRef.current.focus();
            // Preserve cursor position
            const cursorPos = searchQuery.length;
            desktopSearchRef.current.setSelectionRange(cursorPos, cursorPos);
          }
        }
      }, 10);
    };
    
    if (wasSearchFocused) {
      focusSearch();
    }
  }, [activeTab, wasSearchFocused, searchQuery]);
  
  // Track when search input is focused
  const handleSearchFocus = () => {
    setWasSearchFocused(true);
  };

  const filteredChats = chats.filter(chat => 
    (chat.seller_name || `${chat.seller_first_name || ''} ${chat.seller_last_name || ''}`)
      .toLowerCase().includes(searchQuery.toLowerCase()) ||
    (chat.listing_title || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getOtherPartyInfo = (escrow, userId) => {
    if (!escrow || !userId) return { name: '', storeName: '' };
    
    if (escrow.buyer_id === userId) {
      return {
        name: `${escrow.seller_first_name || ''} ${escrow.seller_last_name || ''}`.trim() || 'Seller',
        storeName: escrow.seller_store_name || ''
      };
    } else {
      return {
        name: `${escrow.buyer_first_name || ''} ${escrow.buyer_last_name || ''}`.trim() || 'Buyer',
        storeName: '' // Buyers typically don't have store names
      };
    }
  };

  const filteredEscrowHistory = escrowHistory.filter(escrow => {
    const otherPartyInfo = getOtherPartyInfo(escrow, user?.user_id);
    return (
      otherPartyInfo.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      otherPartyInfo.storeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (escrow.listing_title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      escrow.escrow_id.toString().includes(searchQuery)
    );
  });

;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading text="Loading..." />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign in to view messages</h2>
          <p className="text-gray-600 mb-6">
            Please sign in to access your conversations and escrow history
          </p>
          <button
            onClick={() => navigate('/login')}
            className="bg-[#ff660e] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#ff5500] transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // ========== MOBILE VIEW ==========
  const MobileView = () => (
    <div className="lg:hidden">
      {/* Mobile Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            {/* Back Button */}
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            {/* Title */}
            <h1 className="text-lg font-bold text-black flex-1">Messages</h1>
            
            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          {/* Mobile Search Bar */}
          {/* Mobile Search Bar */}
          <div className="mt-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                ref={mobileSearchRef}
                type="text"
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={handleSearchFocus}
                onBlur={() => {
                  // Don't reset wasSearchFocused on blur, so we can refocus on tab change
                  // setWasSearchFocused(false);
                }}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
              />
            </div>
          </div>
          
          {/* Tabs - Mobile */}
          <div className="flex mt-3 bg-gray-100 p-1 rounded-xl">
            <button
              onClick={() => setActiveTab('chats')}
              className={`flex-1 py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                activeTab === 'chats'
                  ? 'bg-white shadow text-[#ff660e]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              <span className="text-sm font-medium">Chats</span>
            </button>
            <button
              onClick={() => setActiveTab('escrow')}
              className={`flex-1 py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                activeTab === 'escrow'
                  ? 'bg-white shadow text-[#ff660e]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Shield className="w-4 h-4" />
              <span className="text-sm font-medium">Escrow</span>
            </button>
          </div>
        </div>
      </header>

      <main className="px-4 py-4">
        {activeTab === 'chats' ? (
          /* Mobile Chats List */
          <div className="space-y-3">
            {filteredChats.length === 0 ? (
              <EmptyState 
                type="chats" 
                title="No conversations"
                message="Start a conversation with sellers"
                icon={<MessageSquare className="w-16 h-16 text-gray-300" />}
              />
            ) : (
              filteredChats.map((chat) => (
                <div 
                  key={chat.conversation_id} 
                  className="bg-white rounded-xl shadow-sm overflow-hidden mb-3" // Added mb-3 for spacing
                >
                  <ChatListItem
                    key={chat.conversation_id}
                    chat={chat}
                    onClick={() => navigateToChat(chat)}
                    formatTime={formatTime}
                    variant="mobile"
                    currentUserId={user?.user_id}
                  />
 
                </div>
              ))
            )}
          </div>
        ) : (
          /* Mobile Escrow History */
          <div>
            {/* Stats Overview - Mobile */}
            <div className="mb-4">
              <StatsCards stats={stats} formatPrice={formatPrice} variant="mobile" />
            </div>

            {/* Escrow List - Mobile */}
            {filteredEscrowHistory.length === 0 ? (
              <EmptyState 
                type="escrow"
                title="No escrow history"
                message="Your escrow transactions will appear here once you make or receive secure payments"
                icon={<Shield className="w-16 h-16 text-gray-300" />}
              />
            ) : (
              <div className="space-y-3">
                {filteredEscrowHistory.map((escrow) => (
                  <div 
                    key={escrow.escrow_id} 
                    className="bg-white rounded-xl shadow-sm overflow-hidden"
                  >
                  <EscrowListItem
                  escrow={escrow}
                  user={user}
                  onClick={() => navigateToEscrow(escrow)}
                  formatRelativeTime={formatRelativeTime}
                  formatPrice={formatPrice}
                  variant="mobile"
                  otherPartyInfo={getOtherPartyInfo(escrow, user?.user_id)} // Add this
                />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );

  // ========== DESKTOP VIEW ==========
  const DesktopView = () => (
    <div className="hidden lg:block">
      {/* Desktop Header */}
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Search and Tabs */}
        <div className="mb-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                ref={desktopSearchRef}
                type="text"
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={handleSearchFocus}
                onBlur={() => {
                  // Don't reset wasSearchFocused on blur
                  // setWasSearchFocused(false);
                }}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
              />
            </div>
          </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              
              <div className="flex space-x-2 bg-gray-100 p-1 rounded-xl">
                <button
                  onClick={() => setActiveTab('chats')}
                  className={`py-2 px-4 rounded-lg flex items-center gap-2 transition-colors ${
                    activeTab === 'chats'
                      ? 'bg-white shadow text-[#ff660e]'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="font-medium">Chats ({chats.length})</span>
                </button>
                <button
                  onClick={() => setActiveTab('escrow')}
                  className={`py-2 px-4 rounded-lg flex items-center gap-2 transition-colors ${
                    activeTab === 'escrow'
                      ? 'bg-white shadow text-[#ff660e]'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Shield className="w-4 h-4" />
                  <span className="font-medium">Escrow ({stats.total})</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {activeTab === 'chats' ? (
          /* Desktop Chats List */
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-black">Conversations</h2>
              <p className="text-sm text-gray-500">{chats.length} active chats</p>
            </div>
            
            <div className="divide-y divide-gray-100">
              {filteredChats.length === 0 ? (
                <EmptyState 
                  type="chats"
                  title="No conversations yet"
                  message="Start a conversation with sellers by contacting them on listings"
                  icon={<MessageSquare className="w-20 h-20 text-gray-300" />}
                  variant="desktop"
                />
              ) : (
                filteredChats.map((chat) => (
                  <ChatListItem
                    key={chat.conversation_id}
                    chat={chat}
                    onClick={() => navigateToChat(chat)}
                    formatTime={formatTime}
                    variant="desktop"
                    currentUserId={user?.user_id} // Pass current user ID
                  />
                 
                ))
              )}
            </div>
          </div>
        ) : (
          /* Desktop Escrow History */
          <div className="space-y-6">
            {/* Stats Overview - Desktop */}
            <StatsCards stats={stats} formatPrice={formatPrice} variant="desktop" />

            {/* Escrow History List - Desktop */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-bold text-black">Escrow History</h2>
                <p className="text-sm text-gray-500">All your secure transactions</p>
              </div>
              
              <div className="divide-y divide-gray-100">
                {filteredEscrowHistory.length === 0 ? (
                  <EmptyState 
                    type="escrow"
                    title="No escrow history"
                    message="Your escrow transactions will appear here once you make or receive secure payments"
                    icon={<Shield className="w-20 h-20 text-gray-300" />}
                    variant="desktop"
                  />
                ) : (
                  filteredEscrowHistory.map((escrow) => (
                    <EscrowListItem
                    key={escrow.escrow_id}
                    escrow={escrow}
                    user={user}
                    onClick={() => navigateToEscrow(escrow)}
                    formatRelativeTime={formatRelativeTime}
                    formatPrice={formatPrice}
                    variant="desktop"
                    otherPartyInfo={getOtherPartyInfo(escrow, user?.user_id)} // Add this
                  />
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Render Mobile or Desktop View */}
      <MobileView />
      <DesktopView />
      
      
      <StandaloneEscrowModal
  isOpen={showEscrowModal}
  onClose={() => {
    setShowEscrowModal(false);
    setSelectedEscrow(null);
  }}
  escrow={selectedEscrow}
  user={user}
  onRefresh={fetchEscrowHistory}
  onNavigateToChat={navigateToChat}
  onConfirmReceipt={async () => {
    if (!selectedEscrow) return;
    
    try {
      const result = await confirmEscrowReceipt(selectedEscrow.escrow_id, {
        user_id: user.user_id
      });
      
      if (result.success) {
        alert('✅ Payment released to seller!');
        await fetchEscrowHistory();
      } else {
        alert('❌ Error: ' + (result.error || 'Failed to confirm receipt'));
      }
    } catch (error) {
      // console.error('Confirm receipt error:', error);
      alert('❌ Error: Failed to confirm receipt');
    }
  }}
  onRaiseDispute={() => {
    if (!selectedEscrow) return;
    
    navigate(`/disputes/raise`, {
      state: {
        escrowId: selectedEscrow.escrow_id,
        conversationId: selectedEscrow.conversation_id,
        amount: selectedEscrow.amount,
        sellerId: selectedEscrow.seller_id,
        buyerId: selectedEscrow.buyer_id,
        listingTitle: selectedEscrow.listing_title || 'Item',
        fromChat: false,
        fromEscrowList: true
      }
    });
  }}
  // Add this prop to handle viewing existing disputes
  onViewDisputeDetails={() => {
    if (selectedEscrow?.dispute_id) {
      navigate(`/disputes/${selectedEscrow.dispute_id}`);
    }
  }}
/>
      
      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden">
        <MobileBottomNav />
      </div>
    </div>
  );
};

export default ChatsScreen;